import { USER_TYPE } from '../enums/index.ts';
// 强度判据与生成器同源（tempPassword.ts 零 import ⇒ 断言可直接跑本模块真代码）
import { meetsPasswordStrength } from './tempPassword.ts';

/**
 * 「重置口令」入口的角色码判据（t24）。
 *
 * 用户 2026-10-10 明确：平台管理员可重置**除超管外**所有用户的口令 ⇒ 目标是超级管理员时，本入口
 * fail-closed（隐藏入口＋组件侧再拦一道）。判据只取**行上既有角色码字段**：
 * ⛔ 不新写第二套判据源、⛔ 不拿 isExternal / endBinding 去猜。
 */
export const PASSWORD_RESET_BLOCKED_ROLE_CODE: string = USER_TYPE.SUPER_ADMIN;

/** 大写字面比较，容忍首尾空白；空值/非字符串一律**不**判为禁区（缺字段时由调用面自己决定，⛔ 不在此处猜）。 */
export function isPasswordResetBlockedRoleCode(code: string | null | undefined): boolean {
  if (typeof code !== 'string') return false;
  return code.trim().toUpperCase() === PASSWORD_RESET_BLOCKED_ROLE_CODE;
}

/**
 * 重置口令弹窗的目标（三处入口共用同一件组件，故取三面名册字段的并集）。
 *
 * roleCode 由调用方喂**行上既有字段**：平台用户行 = `userType`（与 sys_role.code 同字面）、
 * 交付机构成员行 = `globalRoleCode ?? memberRole`、BPO 机构成员行 = `agencyRole`
 * （机构内角色码，与超管字面永不相交 ⇒ 一律放行）。
 */
export interface PasswordResetTarget {
  id: string;
  account: string;
  name?: string;
  roleCode?: string | null;
}

/** 开窗预置的返回：口令与提示文案（两枚互斥，失败分支口令恒为空串） */
export interface ResetPasswordSeed {
  password: string;
  error: string;
}

/** 禁区目标的提示（超管不提供重置） */
export const RESET_PASSWORD_BLOCKED_NOTE = '目标账号是超级管理员：本入口不提供口令重置';

/** 生成失败（当前环境拿不到安全随机源）的提示：要求可手填，⛔ 静默留空 */
export const RESET_PASSWORD_GENERATE_FAILED_NOTE =
  '没能自动生成口令：当前环境无法安全生成随机口令，请手动填写一枚（至少 8 位，需含大小写字母和数字）';

/**
 * 「开窗／重新生成该往输入框放什么」的**唯一一份**落点（t82）：抽成纯函数，弹窗调用它、断言跑它的真代码，
 * ⛔ 各处自己 try 一遍、⛔ 失败时静默留空（空白面板看着像「还没生成」，也会让人直接点确认）。
 *
 * 三分支：禁区目标 ⇒ 不生成（空串＋禁区提示）；生成成功 ⇒ 预置这枚口令；生成抛错／回空串／**不过强度校验** ⇒
 * 空串＋可手填提示。t141（F3）：出口自己按 meetsPasswordStrength 验收（有界重试 RESET_PASSWORD_SEED_ATTEMPTS 次），
 * ⛔ 不再把「生成器的内部契约」当保证——不过校验的值一律不预置（否则重开对话框会拿到一枚提交时必被拦的口令）。
 * ⛔ 任何分支都不回落伪随机口令（弱随机＝可预测）。
 */
export const RESET_PASSWORD_SEED_ATTEMPTS = 3;

export function seedResetPasswordPanel(input: {
  blocked: boolean;
  generate: () => string;
  attempts?: number;
}): ResetPasswordSeed {
  if (input.blocked) return { password: '', error: RESET_PASSWORD_BLOCKED_NOTE };
  // 有界重试：生成器可能偶尔给不出合规值（换随机源、换实现、被替换成桩）——重试几次仍不行就走失败提示，
  // ⛔ 把不合规的口令预置上屏（那正是 F3：重开后拿到不过强度校验的值，而唯一的强度闸在提交那一刻）。
  const attempts = Math.max(1, Math.min(5, Math.trunc(input.attempts ?? RESET_PASSWORD_SEED_ATTEMPTS)));
  for (let i = 0; i < attempts; i += 1) {
    let candidate = '';
    try {
      candidate = input.generate();
    } catch {
      return { password: '', error: RESET_PASSWORD_GENERATE_FAILED_NOTE };
    }
    if (typeof candidate !== 'string' || candidate === '') continue;
    if (!meetsPasswordStrength(candidate)) continue;
    return { password: candidate, error: '' };
  }
  return { password: '', error: RESET_PASSWORD_GENERATE_FAILED_NOTE };
}
